import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  GRID_LEFT_SECTIONS,
  GRID_RIGHT_SECTIONS,
  SECTION_HEADINGS,
  formatAnswerForDisplay,
  getActiveSteps,
  getTastingSteps,
  groupStepsBySection,
} from "../../utils/wineTastingGrid";
import { comparisonCellSx } from "../../utils/wineTastingCompare";

function SectionBlock({ section, steps, answers, comparisons, expected }) {
  const title = SECTION_HEADINGS[section] || section;
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        mb: 1.5,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          bgcolor: "#1a1a1a",
          color: "#fff",
          px: 1.5,
          py: 0.85,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.8rem" }}
        >
          {title}
        </Typography>
      </Box>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {steps.map((step, index) => {
            const value = formatAnswerForDisplay(answers[step.id]);
            if (!value && step.skippable) return null;
            const status = comparisons?.[step.id] || null;
            const expectedLabel = expected?.[step.id];
            const fallbackBg = index % 2 === 0 ? "grey.50" : "background.paper";
            const titleHint =
              status && expectedLabel
                ? `${status === "match" ? "Match" : "Afviger"} · GrapeMinds: ${expectedLabel}`
                : status === "match"
                  ? "Match med GrapeMinds"
                  : status === "mismatch"
                    ? "Afviger fra GrapeMinds"
                    : undefined;
            return (
              <Box
                component="tr"
                key={step.id}
                title={titleHint}
                sx={{
                  ...comparisonCellSx(status, fallbackBg),
                  verticalAlign: "top",
                }}
              >
                <Box
                  component="th"
                  scope="row"
                  sx={{
                    textAlign: "left",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    px: 1.25,
                    py: 1,
                    width: "38%",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    color: "text.primary",
                  }}
                >
                  {step.label}
                </Box>
                <Box
                  component="td"
                  sx={{
                    px: 1.25,
                    py: 1,
                    fontSize: "0.9rem",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    color: value ? "text.primary" : "text.disabled",
                    lineHeight: 1.45,
                  }}
                >
                  {value || "—"}
                  {status && expectedLabel ? (
                    <Typography
                      component="span"
                      variant="caption"
                      display="block"
                      sx={{ mt: 0.35, color: "text.secondary" }}
                    >
                      GM: {expectedLabel}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

function Column({ sections, groups, answers, comparisons, expected }) {
  return (
    <Box>
      {sections.map((section) => {
        const group = groups.find((g) => g.section === section);
        if (!group || !group.steps.length) return null;
        return (
          <SectionBlock
            key={section}
            section={section}
            steps={group.steps}
            answers={answers}
            comparisons={comparisons}
            expected={expected}
          />
        );
      })}
    </Box>
  );
}

/**
 * CMS-style tasting grid summary table.
 * @param {"tasting"|"all"} mode tasting = before final conclusion; all = full sheet
 */
export default function WineTastingGridView({
  answers = {},
  wine = null,
  mode = "all",
  blind = true,
  title = "Deduktiv smagning",
  comparisons = null,
  expected = null,
}) {
  const steps = useMemo(
    () =>
      mode === "tasting"
        ? getTastingSteps(answers, { blind })
        : getActiveSteps(answers, { blind }),
    [answers, mode, blind]
  );
  const groups = useMemo(() => groupStepsBySection(steps), [steps]);

  const leftSections = GRID_LEFT_SECTIONS;
  const rightSections = useMemo(() => {
    let sections =
      mode === "tasting"
        ? GRID_RIGHT_SECTIONS.filter((s) => s !== "Endelig konklusion")
        : [...GRID_RIGHT_SECTIONS];
    if (!blind) {
      sections = sections.filter(
        (s) => s !== "Foreløbig konklusion" && s !== "Endelig konklusion"
      );
    }
    return sections;
  }, [mode, blind]);

  const hasCompare = Boolean(comparisons && Object.keys(comparisons).some((k) => k !== "__expected"));
  const showWine = Boolean(wine && (wine.name || wine.grapeminds_id));
  const wineHeading = blind ? "Vin afsløret" : "Vin";

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          px: 1.5,
          py: 1.25,
          mb: 1.5,
          bgcolor: "grey.100",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 800, letterSpacing: "0.02em", textTransform: "uppercase", fontSize: "0.95rem" }}
        >
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Court of Master Sommeliers — Advanced/Master-format
        </Typography>
        {hasCompare ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Grøn = match med GrapeMinds · Rød = stor afvigelse · Ufarvet = ingen sikker reference
          </Typography>
        ) : null}
      </Box>

      {showWine ? (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 1.5,
            overflow: "hidden",
          }}
        >
          <Box sx={{ bgcolor: "#1a1a1a", color: "#fff", px: 1.5, py: 0.85 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.8rem" }}
            >
              {wineHeading}
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {wine.name || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[wine.producer, wine.region, wine.grape, wine.vintage].filter(Boolean).join(" · ") || "—"}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 0, md: 2 },
          alignItems: "start",
        }}
      >
        <Column
          sections={leftSections}
          groups={groups}
          answers={answers}
          comparisons={comparisons}
          expected={expected}
        />
        <Column
          sections={rightSections}
          groups={groups}
          answers={answers}
          comparisons={comparisons}
          expected={expected}
        />
      </Box>
    </Box>
  );
}
